import uniqid from 'uniqid';
import clone from 'lodash/clone';
import { getProjectState, setProjectState } from '@starflow/app/store';
import { convertGroupToImage } from '@shakkerai/pro-utils';
import { getCanvasCenter } from './canvas';

export const addELementsToGroup = (groupId, elements) => {};
export const removeElementsFromGroup = (groupId, elements) => {};

export const getNextName = (list, type = 'Rect') => {
  const filtered = list
    .reduce((result, item) => {
      result.push(item);
      if (item.type === 'group' && item.elements?.length) {
        result.push(...item.elements);
      }
      return result;
    }, [])
    .map((item) => {
      const [, num] = item.name?.match(new RegExp(`^${type}\\s*(\\d{0,5})`)) || [];
      return +num;
    })
    .filter((i) => i);

  return `${type} ${Math.max(...filtered, 0) + 1}`;
};

export const findElement = (elements, id) => {
  let target = null;

  elements.some((element) => {
    if (element.id === id) return (target = element);
    if (
      element.elements?.some((child) => {
        if (child.id === id) return (target = element);
      })
    ) {
      return true;
    }
  });
  return target;
};

export const cleanElements = () => {
  setProjectState({ elements: [] });
};

// 在底部空间查找合适的插入位置
export const findInsertPosition = (rect, gap = 10) => {
  let { x, y, width, height } = rect;
  const elements = getProjectState('elements');
  const center = getCanvasCenter();

  typeof x !== 'number' && (x = center.x - width / 2);
  typeof y !== 'number' && (y = center.y - height / 2);

  const nextElements = elements
    .filter((ele) => {
      return !(ele.x + ele.width * (ele.scaleX || 1) < x || ele.x > x + width);
    })
    .filter((ele) => {
      return !(ele.y + ele.height * (ele.scaleY || 1) + gap < y);
    })
    .sort((a, b) => a.y + a.height * (a.scaleY || 1) - (b.y + b.height * (b.scaleY || 1)));

  let finalY = y;
  let finalX = x;

  let hasIntersectionElements = nextElements.some((ele) => {
    return !(finalY + height + gap <= ele.y || finalY >= ele.y + ele.height * (ele.scaleY || 1) + gap);
  });

  if (!hasIntersectionElements) {
    return [finalX, finalY];
  }

  while (hasIntersectionElements) {
    const lastEle = nextElements[0];
    finalY = lastEle.y + lastEle.height * (lastEle.scaleY || 1) + gap;
    finalX = lastEle.x;
    nextElements.shift();
    hasIntersectionElements = nextElements.some((ele) => {
      return !(finalY + height + gap <= ele.y || finalY >= ele.y + ele.height * (ele.scaleY || 1) + gap);
    });
  }

  return findInsertPosition({ x: finalX, y: finalY, width, height }, gap);
};

// 在右侧空间查找合适的插入位置
export const findRightInsertOffset = (position, elements, gap = 10) => {
  const { x, y, width, height } = position;

  const nextElements = elements
    .filter((ele) => {
      return !(ele.y + ele.height * (ele.scaleY || 1) < y || ele.y > y + height);
    })
    .filter((ele) => {
      return !(ele.x + ele.width * (ele.scaleX || 1) + gap < x);
    })
    .sort((a, b) => a.x + a.width * (a.scaleX || 1) - (b.x + b.width * (b.scaleX || 1)));

  let targertX = x;

  let hasIntersectionElements = nextElements.some((ele) => {
    return !(targertX + width + gap <= ele.x || targertX >= ele.x + ele.width * (ele.scaleX || 1) + gap);
  });

  if (!hasIntersectionElements) {
    return targertX;
  }

  while (hasIntersectionElements) {
    const lastEle = nextElements[0];
    targertX = lastEle.x + lastEle.width * (lastEle.scaleX || 1) + gap;
    nextElements.shift();
    hasIntersectionElements = nextElements.some((ele) => {
      return !(targertX + width + gap <= ele.x || targertX >= ele.x + ele.width * (ele.scaleX || 1) + gap);
    });
  }

  return findRightInsertOffset({ x: targertX, y, width, height }, elements, gap);
};

export const ungroup = (group) => {
  return group.elements.map((el) => {
    const x = group.x + el.x * group.scaleX;
    const y = group.y + el.y * group.scaleY;
    const scaleX = el.scaleX * group.scaleX;
    const scaleY = el.scaleY * group.scaleY;
    return Object.assign({}, el, { x, y, scaleY, scaleX });
  });
};

export const makeGroup = (children, groupProps = {}) => {
  const position = getGroupPosition(children);

  children = children.map((el) => {
    const x = el.x - position.left;
    const y = el.y - position.top;
    return Object.assign({}, el, { x, y });
  });

  return Object.assign(
    {},
    {
      id: `element-group-${uniqid()}`,
      key: `element-group-key-${uniqid()}`, // group组件key不变的话，操作组内元素会造成渲染不完全更新的问题
      type: 'group',
      x: position.left,
      y: position.top,
      scaleX: 1,
      scaleY: 1,
      width: position.right - position.left,
      height: position.bottom - position.top,
      elements: children,
      ...groupProps,
    },
  );
};

export const groupElements = (elementIds, elements) => {
  elements = elements || getProjectState().elements.slice();
  const elementIdsMap = Object.fromEntries(elementIds.map((i) => [i, true]));
  const targetElements = [];

  let targetIndex = -1;

  const nextElements = elements.reduce((res, element, index) => {
    if (elementIdsMap[element.id]) {
      targetIndex = index;
      if (element.elements) {
        targetElements.push(...ungroup(element));
      } else {
        targetElements.push(element);
      }
      res.push(null);
    } else if (element.elements) {
      const children = element.elements.filter((child) => !elementIdsMap[child.id]);
      const hitGroup = children.length < element.elements.length;
      if (hitGroup) {
        targetIndex = index;
        const preChildren = ungroup(element);
        switch (children.length) {
          case 0:
            targetElements.push(...preChildren);
            res.push(null);
            break;
          case 1:
            targetElements.push(...preChildren.filter((child) => elementIdsMap[child.id]));
            res.push(...preChildren.filter((child) => !elementIdsMap[child.id]));
            break;
          default:
            targetElements.push(...preChildren.filter((child) => elementIdsMap[child.id]));
            res.push(
              makeGroup(
                preChildren.filter((child) => !elementIdsMap[child.id]),
                {
                  name: element.name,
                  id: element.id,
                },
              ),
            );
        }
      } else {
        res.push(element);
      }
    } else {
      res.push(element);
    }
    return res;
  }, []);

  const nextGroup = makeGroup(targetElements, {
    name: getNextName(elements, 'Group'),
  });

  nextElements.splice(targetIndex, 0, nextGroup);
  return [nextElements.filter((i) => i), nextGroup];
};

export const ungroupElements = (elementIds, elements) => {
  elements = elements || getProjectState().elements.slice();
  const elementIdsMap = Object.fromEntries(elementIds.map((id) => [id, true]));
  return elements.reduce((res, element) => {
    if (elementIdsMap[element.id] && element.elements) {
      res.push(...ungroup(element));
    } else {
      res.push(element);
    }
    return res;
  }, []);
};

export const regroupElements = (groupId, preUpdate, elements) => {
  if (typeof preUpdate === 'function') {
    elements = preUpdate(elements);
  }
  const [nextElements, group] = ungroupElements(groupId, elements);
  const elementIds = group.elements.map((el) => el.id);
  const [finalElements] = groupElements(elementIds, nextElements, {
    id: group.id,
    name: group.name,
  });
  return finalElements;
};

export const mapUpdateElements = (elements, elementsMap) => {
  return elements.map((element) => {
    if (elementsMap[element.id]) {
      const updatedData = elementsMap[element.id];
      const nextData = Object.assign({}, element, updatedData);

      // 如果url变了，就更新一下时间戳
      if (updatedData.url) nextData.t = Date.now();

      return Object.assign(nextData);
    } else if (element.elements) {
      let hitGroup = null;
      let needRegroup = false;
      const children = element.elements.map((child) => {
        const target = elementsMap[child.id];
        if (target) {
          hitGroup = element.id;
          needRegroup = ['x', 'y', 'scaleX', 'scaleY'].some((key) => target[key] !== undefined);
          return Object.assign({}, child, target);
        }
        return child;
      });
      const newGroup = Object.assign({}, element, { elements: children });
      if (needRegroup) {
        return regroup(newGroup);
      } else if (hitGroup) {
        return newGroup;
      }
    }
    return element;
  });
};

export const regroup = (group) => {
  const children = ungroup(group);

  return makeGroup(children, {
    id: group.id,
    name: group.name,
    collage: group.collage,
  });
};

export const removeChildElements = (group, targetIdsMap) => {
  if (!group.elements.some((child) => targetIdsMap[child.id])) {
    return [group, []];
  }

  const children = ungroup(group);

  const [targetElements, nextChildren] = children.reduce(
    (res, child) => {
      res[+!targetIdsMap[child.id]].push(child);
      return res;
    },
    [[], []],
  );
  switch (nextChildren.length) {
    case 0:
      group = null;
      break;
    case 1:
      group = Object.assign({}, nextChildren[0]);
      break;
    default:
      group = makeGroup(nextChildren, {
        id: group.id,
        name: group.name,
      });
  }

  return [group, targetElements];
};

export const addChildElements = (group, sourceElements, index = 0, replaceTarget = false) => {
  sourceElements = sourceElements.filter((child) => child.id !== group.id);
  if (!sourceElements?.length) {
    return group;
  }
  const children = ungroup(group);
  children.splice(
    index,
    replaceTarget ? 1 : 0,
    ...sourceElements.reduce((res, element) => {
      return res.concat(element.elements ? ungroup(element) : element);
    }, []),
  );

  return makeGroup(children, {
    id: group.id,
    name: group.name,
  });
};

// 获取元素在panel中显示的x,y,scaleX,scaleY等，
// group内的要做额外计算
export const getElementRect = (element, group) => {
  let { x, y, width, height, scaleX, scaleY } = element;

  const props = { x, y, width, height, scaleX, scaleY };

  if (group) {
    Object.assign(props, {
      x: Math.round(group.x + element.x * group.scaleX),
      y: Math.round(group.y + element.y * group.scaleY),
      scaleX: element.scaleX * group.scaleX,
      scaleY: element.scaleY * group.scaleY,
    });
  } else {
    Object.assign(props, {
      x: Math.round(x),
      y: Math.round(y),
    });
  }
  return props;
};

// 获取元素在画布中渲染时的真实x,y,scaleX,scaleY等，
// group中元素需要减去group本身的位置（相对偏移量）
export const getElementRenderedRect = (props, group, options = null) => {
  let invalid = false; // 宽高为0时无效
  const getXY = (key) => {
    if (isNaN(props[key])) return (invalid = true);
    return group ? (props[key] - group[key]) / group[`scale${key.toUpperCase()}`] : +props[key];
  };

  const getScaleXY = (key) => {
    if (isNaN(props[key]) || !+props[key]) return (invalid = true);
    let scaleValue = Math.abs(group ? props[key] / group[key] : props[key]);
    scaleValue = scaleValue / (options?.[{ scaleX: 'width', scaleY: 'height' }[key]] || 1);
    if (options?.lockRatio && options?.ratio) {
      switch (key) {
        case 'scaleX':
          props.scaleY = scaleValue / options.ratio;
          break;
        case 'scaleY':
          props.scaleX = scaleValue * options.ratio;
          break;
      }
    }
    return scaleValue;
  };

  const funMap = {
    x: () => getXY('x'),
    y: () => getXY('y'),
    scaleX: () => getScaleXY('scaleX'),
    scaleY: () => getScaleXY('scaleY'),
  };

  Object.keys(props).forEach((key) => {
    props[key] = funMap[key] ? funMap[key]() : props[key];
  });

  return invalid ? null : props;
};

let prevDuplicateIds = null;
let prevDuplicateOffset = 60;

export const calcMultiElementsRect = (images) => {
  const position = { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity };

  if (!images?.length) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
  }

  images.forEach((el) => {
    const { x: left, y: top, width, height, scaleX, scaleY } = el;

    const right = left + width * scaleX;
    const bottom = top + height * scaleY;
    if (scaleX < 0) {
      if (right < position.left) position.left = right;
      if (left > position.right) position.right = left;
    } else {
      if (left < position.left) position.left = left;
      if (right > position.right) position.right = right;
    }
    if (scaleY < 0) {
      if (bottom < position.top) position.top = top + bottom;
      if (top > position.bottom) position.bottom = top;
    } else {
      if (top < position.top) position.top = top;
      if (bottom > position.bottom) position.bottom = bottom;
    }
  });

  return {
    x: position.left,
    y: position.top,
    width: Math.round(position.right - position.left),
    height: Math.round(position.bottom - position.top),
  };
};

export const duplicateElements = (elementIds) => {
  const elementIdsMap = Object.fromEntries(
    elementIds.filter((item) => item.startsWith('element')).map((id) => [id, true]),
  );
  const elements = getProjectState().elements;
  const elementsAllowPaste = elements.filter((element) => elementIdsMap[element.id]);

  if (prevDuplicateIds === elementIds) {
    prevDuplicateOffset += 60;
  } else {
    prevDuplicateOffset = 60;
    prevDuplicateIds = elementIds;
  }

  const pastedElements = elementsAllowPaste.map((element) => {
    const clonedElement = clone(element);
    clonedElement.id = `element-${element.type}-${uniqid()}`;
    clonedElement.x += prevDuplicateOffset;
    clonedElement.y += prevDuplicateOffset;

    if (clonedElement.inpaint) {
      clonedElement.inpaint.enabled = false;
      clonedElement.inpaint.generatorId = null;
    }

    return clonedElement;
  });

  setProjectState({
    elements: elements.concat(pastedElements),
    selection: pastedElements.map((element) => element.id),
  });
};

export const getGroupPosition = (children = []) => {
  const position = { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity };

  children.forEach((el) => {
    const { x: left, y: top, width, height, scaleX, scaleY } = el;

    const right = left + width * scaleX;
    const bottom = top + height * scaleY;
    if (scaleX < 0) {
      if (right < position.left) position.left = right;
      if (left > position.right) position.right = left;
    } else {
      if (left < position.left) position.left = left;
      if (right > position.right) position.right = right;
    }
    if (scaleY < 0) {
      if (bottom < position.top) position.top = top + bottom;
      if (top > position.bottom) position.bottom = top;
    } else {
      if (top < position.top) position.top = top;
      if (bottom > position.bottom) position.bottom = bottom;
    }
  });

  return position;
};

// 多张图片合并成一张图片, 简单处理分三步: 第一步groupElements, 第二步groupToImage, 第三步ungroup
export const convertMultiSelectionToImage = async (elementIds) => {
  let groupId;
  console.log('xxx convertMultiSelectionToImage');
  const isgroup = elementIds.length === 1 && elementIds[0].includes('group');
  if (!isgroup) {
    const [elements, group] = groupElements(elementIds);
    setProjectState({ elements: elements, selection: [] });
    await new Promise((resolve) => requestAnimationFrame(resolve));
    groupId = group.id;
  } else {
    groupId = elementIds[0];
  }
  await convertGroupToImage(groupId);
  if (!isgroup) {
    const newElements = ungroupElements([groupId]);
    setProjectState({ elements: newElements, selection: [] });
  } else {
    setProjectState({ selection: [] });
  }
};

export const getElementById = (id) => {
  return getProjectState().elements.find((element) => element.id === id);
};
